import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';

/**
 * The one place the UI learns which currency it is looking at (multi-currency Phase 2).
 *
 * Every money figure on every screen used to be labelled by a pipe that hard-coded the string ' USD'. That is
 * true today and will be wrong the moment a client is billed in ZiG: a figure in one book would be shown wearing
 * the name of another. The pipes now ask this service instead.
 *
 * ── WHY IT ANSWERS SYNCHRONOUSLY ────────────────────────────────────────────────────────────────────────────
 * A pipe cannot await. So the context is fetched once and cached, and `base` answers immediately from that cache
 * — falling back to USD until the first answer arrives. That fallback is not a guess: USD IS the base on every
 * tenant today, and it stays the base after Phase 2. The only thing the fetch can change is the LIST of
 * currencies and whether non-base settlement is switched on.
 *
 * ── AND WHY A FAILED LOAD IS SILENT ─────────────────────────────────────────────────────────────────────────
 * `/currencies/context` needs a bearer token, so it is not callable on the login screen. A failure there — or a
 * network blip — must never break a page whose only need was to print "$9.00". It leaves the cache empty, the
 * base at USD, and tries again on the next ask. The API remains the single source of truth: nothing here
 * computes money, it only decides what to call it.
 */
@Injectable({
  providedIn: 'root',
})
export class CurrencyContextService {
  /** The base every tenant runs on today, and the base Phase 2 keeps. Used until the API answers. */
  public static readonly FALLBACK_BASE = 'USD';

  /** What a figure reads when its amount is unknown — the same marker the API prints on a document. */
  public static readonly UNAVAILABLE = '—';

  private currentDomain = environment.currentApiHostname;

  private context: {
    base: string;
    active: string;
    currencies: string[];
    settlement_enabled: boolean;
    rows_govern_base: boolean;
  } | null = null;

  private inFlight = false;

  constructor(private http: HttpClient) {}

  /** The base currency's code. Answers immediately; USD until the API has said otherwise. */
  get base(): string {
    this.ensureLoaded();

    return this.context?.base || CurrencyContextService.FALLBACK_BASE;
  }

  /**
   * The book a money row is in: its own stamp, or the base when it carries none.
   *
   * A blank or NULL currency on a money row IS base money — every row written before multi-currency stores
   * nothing there, and so does every base row since. This is the ONE place that is decided, because the
   * alternative is each screen writing `row.currency || 'USD'` for itself and one of them eventually not
   * doing it. That is precisely how a ZiG truck came to print a receipt reading "68,960.00 USD".
   *
   * Pair it with the `customCurrency` pipe, which needs a real code; `defaultCurrency` is base money only.
   */
  bookOf(currency?: string | null): string {
    const code = (currency ?? '').trim().toUpperCase();

    return code === '' ? this.base : code;
  }

  /** Is non-base settlement switched on? While false the UI must not offer a currency choice anywhere. */
  get settlementEnabled(): boolean {
    this.ensureLoaded();

    return !!this.context?.settlement_enabled;
  }

  /**
   * Every currency this system KNOWS, base first — not the same question as which ones may be chosen.
   * `/currencies/context` returns the full list whatever the settlement switch says, so a screen offering a
   * choice must use {@link settleable}, not this.
   */
  get currencies(): string[] {
    this.ensureLoaded();

    const base = this.base;
    const all = this.context?.currencies ?? [base];

    return [base, ...all.filter((c) => c !== base)];
  }

  /**
   * The books an operator may actually pick, base first — just the base while settlement is off.
   *
   * This mirrors a refusal, it is not a nicety. `SettlementCurrency::forTender` on the API rejects any
   * non-base book while the switch is off, so a screen that offered ZiG today would be offering a choice
   * whose only outcome is a 422. For a warehouse contract it would be worse than a dead end: the contract's
   * book is what the monthly billing cron raises its invoice in, so a contract parked on a book the system
   * cannot settle in is a landmine on a money path that runs unattended.
   */
  get settleable(): string[] {
    const base = this.base;

    return this.settlementEnabled ? this.currencies : [base];
  }

  /**
   * Is a base price edited on the price list, or on the record itself?
   *
   * False until the Phase 2 gate, which is why the Price List screen must not offer an editor for a base cell:
   * a base price saved there would be kept and then ignored by every charge.
   */
  get rowsGovernBase(): boolean {
    this.ensureLoaded();

    return !!this.context?.rows_govern_base;
  }

  /** Is this code the base book? Trimmed and case-folded on both sides, as the API does. */
  isBase(code: string | null | undefined): boolean {
    const normalised = (code ?? '').trim().toUpperCase();

    return normalised === '' || normalised === this.base.trim().toUpperCase();
  }

  /** Forget the cache — for a tenant switch or a sign-out. */
  reset(): void {
    this.context = null;
    this.inFlight = false;
  }

  private ensureLoaded(): void {
    if (this.context || this.inFlight) {
      return;
    }
    // The endpoint is authenticated, so there is nothing to ask before sign-in.
    if (!localStorage.getItem('access_token')) {
      return;
    }

    this.inFlight = true;
    this.http
      .get<{
        base: string;
        active: string;
        currencies: string[];
        settlement_enabled: boolean;
        rows_govern_base: boolean;
      }>(`https://${this.currentDomain}/api/currencies/context`, {
        headers: new HttpHeaders({
          Accept: 'application/json',
          Authorization: 'Bearer ' + localStorage.getItem('access_token'),
        }),
      })
      .subscribe({
        next: (ctx) => {
          this.context = ctx;
          this.inFlight = false;
        },
        error: () => {
          // Silent on purpose: a page that only needed to print "$9.00" must not break because this failed.
          this.inFlight = false;
        },
      });
  }
}
